if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navPrevious: this.querySelector('[data-media-nav="previous"]'),
          navNext: this.querySelector('[data-media-nav="next"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Left/Right arrow-key navigation for the main image viewer. Bound
        // to this element only, so it fires from bubbled keydown events
        // that originate inside the gallery (the gallery itself via its
        // tabindex, a thumbnail button, the zoom trigger, etc). It never
        // sees keydowns from the search box, a quantity field, the cart
        // note, or anything else outside this element -- a document-level
        // listener would steal arrow keys used for page scrolling and
        // text-field caret movement everywhere else on the page, so this
        // is intentionally never bound to `document`. onKeyDown
        // additionally ignores text inputs as a second guard.
        this.addEventListener('keydown', this.onKeyDown.bind(this));

        // Clicking anywhere in the gallery (including the main image,
        // which isn't always itself a focusable element) moves focus onto
        // the gallery region so Left/Right work immediately afterwards,
        // without requiring an extra Tab press first.
        this.addEventListener('click', this.onGalleryClick.bind(this));

        if (this.elements.navPrevious) {
          this.elements.navPrevious.addEventListener('click', () => this.stepActiveMedia(-1));
        }
        if (this.elements.navNext) {
          this.elements.navNext.addEventListener('click', () => this.stepActiveMedia(1));
        }

        // Keep the active-media state in sync with the *native* touch
        // swipe on the mobile slider too (SliderComponent dispatches this
        // when the user physically scrolls the strip), so the arrows /
        // arrow keys always agree with what's on screen.
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        this.updateNavButtons();

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onGalleryClick() {
        if (this.contains(document.activeElement)) return;
        this.focus();
      }

      onKeyDown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.defaultPrevented) return;

        const target = event.target;
        const tagName = target && target.tagName;
        if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || (target && target.isContentEditable)) {
          return;
        }

        const direction = event.key === 'ArrowLeft' ? -1 : 1;
        if (!this.stepActiveMedia(direction)) return;

        event.preventDefault();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;

        // The native swipe only scrolls the strip -- it doesn't mark a new
        // .is-active item the way setActiveMedia does, so do that here too
        // or getActiveMediaId() would report a stale slide after a swipe.
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element.dataset.mediaId === mediaId);
        });

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

        this.updateNavButtons();
      }

      // Canonical media order: the thumbnail rail's data-target order when
      // thumbnails exist (that's what the user visually sees as "next"),
      // otherwise fall back to the order media appears in the viewer.
      getOrderedMediaIds() {
        const source = this.elements.thumbnails
          ? this.elements.thumbnails.querySelectorAll('[data-target]')
          : this.elements.viewer.querySelectorAll('[data-media-id]');
        return Array.from(source).map((element) => element.dataset.target || element.dataset.mediaId);
      }

      getActiveMediaId() {
        const activeViewerItem = this.elements.viewer && this.elements.viewer.querySelector('[data-media-id].is-active');
        if (activeViewerItem) return activeViewerItem.dataset.mediaId;

        const activeThumbnail = this.elements.thumbnails && this.elements.thumbnails.querySelector('button[aria-current]');
        const activeThumbnailItem = activeThumbnail && activeThumbnail.closest('[data-target]');
        return activeThumbnailItem ? activeThumbnailItem.dataset.target : null;
      }

      // Steps the active media forward (1) or back (-1) through the same
      // ordered list the thumbnails use, reusing the exact code path a
      // thumbnail click uses (setActiveMedia) so desktop thumbnail-swap
      // and mobile slider-scroll both keep working. Stops at the ends
      // (does not wrap) to match the existing disabled-button convention
      // used elsewhere in the gallery; the caller is responsible for
      // reflecting that in the UI via updateNavButtons(). Returns false
      // when there was nothing to step to.
      stepActiveMedia(direction) {
        if (!this.elements.viewer) return false;

        const mediaIds = this.getOrderedMediaIds();
        if (mediaIds.length < 2) return false;

        const activeIndex = mediaIds.indexOf(this.getActiveMediaId());
        const currentIndex = activeIndex === -1 ? 0 : activeIndex;
        const nextIndex = currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= mediaIds.length) return false;

        this.setActiveMedia(mediaIds[nextIndex], false);
        return true;
      }

      // Disables (and visually hides, via the [disabled] selector in
      // product-media-gallery.liquid) whichever custom nav button would
      // otherwise step past the first/last image. If there's only one
      // image, or no nav buttons were rendered at all (media_count <= 1),
      // this is a no-op.
      updateNavButtons() {
        if (!this.elements.navPrevious && !this.elements.navNext) return;

        const mediaIds = this.getOrderedMediaIds();
        const activeIndex = mediaIds.indexOf(this.getActiveMediaId());

        if (this.elements.navPrevious) {
          this.elements.navPrevious.disabled = mediaIds.length < 2 || activeIndex <= 0;
        }
        if (this.elements.navNext) {
          this.elements.navNext.disabled = mediaIds.length < 2 || activeIndex === -1 || activeIndex >= mediaIds.length - 1;
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        this.updateNavButtons();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // Scrolls the thumbnail rail so the active thumbnail is in view.
      // The rail can be laid out either axis depending on viewport (a
      // vertical stacked column on desktop product pages, a horizontal
      // strip on mobile), so the scroll axis is detected at runtime from
      // the slider's own scrollable overflow rather than assumed from a
      // breakpoint. If the slider doesn't overflow in either direction
      // (everything already fits on screen) this does nothing. Uses
      // slider.scrollTo(...) rather than scrollIntoView so this can never
      // move the surrounding page -- only the thumbnail rail itself.
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const slider = this.elements.thumbnails.slider;
        if (!slider) return;

        const scrollsVertically = slider.scrollHeight > slider.clientHeight;
        const scrollsHorizontally = slider.scrollWidth > slider.clientWidth;
        if (!scrollsVertically && !scrollsHorizontally) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = prefersReducedMotion ? 'auto' : 'smooth';

        if (scrollsVertically) {
          slider.scrollTo({ top: thumbnail.offsetTop, behavior: behavior });
        } else {
          slider.scrollTo({ left: thumbnail.offsetLeft, behavior: behavior });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
